import type { ChartDataPoint, TopPerformerInfo, Performer } from '../types';

/**
 * Maps game history API response to ChartDataPoint array
 */
export const mapGameHistoryResponse = (result: any): ChartDataPoint[] => {
  if (!result?.dataPoints) return [];

  return result.dataPoints.map((point: any) => ({
    label: point.label,
    date: point.date,
    // Started counts
    daily: point.dailyChallenge || 0,
    dailyCompleted: point.dailyChallengeCompleted || 0,
    dailyFailed: point.dailyChallengeFailed || 0,
    vsRandom: point.vsRandom || 0,
    vsFriends: point.vsFriends || 0,
    vsBot: point.vsBot || 0,
    single: point.singleMode || 0,
    // Total VS games from DailyActivityLog (or GameBoard for today)
    vsGamesStarted: point.vsGamesStarted || 0,
    vsGamesCompleted: point.vsGamesCompleted || 0,
    // Completed counts
    vsRandomCompleted: point.vsRandomCompleted || 0,
    vsFriendsCompleted: point.vsFriendsCompleted || 0,
    vsBotCompleted: point.vsBotCompleted || 0,
    singleCompleted: point.singleModeCompleted || 0,
    onlineUsers: point.onlineUsers || 0,
  }));
};

/**
 * Maps top performers API response to Performer array
 */
export const mapTopPerformersResponse = (result: any): Performer[] => {
  if (!result?.success) return [{ name: 'No data', mode: '-', wins: 0 }];

  const performers: Performer[] = [];

  if (result.vsModeTop) {
    performers.push({
      name: result.vsModeTop.username || 'Unknown',
      mode: 'VS Mode',
      wins: result.vsModeTop.wins,
      userId: result.vsModeTop.userId,
      icon: result.vsModeTop.icon,
    });
  }

  if (result.dailyChallengeTop) {
    performers.push({
      name: result.dailyChallengeTop.username || 'Unknown',
      mode: 'Daily Challenge',
      wins: result.dailyChallengeTop.wins,
      userId: result.dailyChallengeTop.userId,
      icon: result.dailyChallengeTop.icon,
    });
  }

  if (result.singleModeTop) {
    performers.push({
      name: result.singleModeTop.username || 'Unknown',
      mode: 'Single Mode',
      wins: result.singleModeTop.wins,
      userId: result.singleModeTop.userId,
      icon: result.singleModeTop.icon,
    });
  }

  if (result.botModeTop) {
    performers.push({
      name: result.botModeTop.username || 'Unknown',
      mode: 'VS Bot',
      wins: result.botModeTop.wins,
      userId: result.botModeTop.userId,
      icon: result.botModeTop.icon,
    });
  }

  return performers.length > 0 ? performers : [{ name: 'No data', mode: '-', wins: 0 }];
};

/**
 * Calculate percentage change between two values
 */
export const calculatePercentageChange = (previous: number, current: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100 * 100) / 100;
};
